/**
 * Adapter for the V3 customer-equipment wire array (Phase 6.2, PROF-04).
 *
 * Wire-contract source (read 2026-06-17):
 *   - `[VERIFIED: ../api/app/helpers/customers_helper.rb:63
 *     links.fetch_equipment → v3_customer_equipment_index_url]`
 *   - `[VERIFIED: ../api/app/views/v3/equipment/_equipment.json.jbuilder]`
 *
 * The list is fetched via the server-minted HATEOAS link
 * `user.links.fetch_equipment` (=== `GET /v3/customers/:id/equipment`) — the
 * service passes that URL VERBATIM and never reconstructs a `/v3/.../equipment`
 * path (Pitfall 2 / threat T-06.2-02). This adapter is a pure wire→domain map;
 * the equipment wire shape is net-new (no prior equipment adapter), so the
 * `EquipmentWire` interface and the map below are new code modeled on the
 * `payment-methods.ts` doc-comment-with-VERIFIED-source convention.
 *
 * Field map (wire → domain): id, equipment_type_name, name, notes, archived,
 * address, components[], warranty (object | null), service_history[].
 */

/** Domain warranty block, or null when the equipment carries no warranty. */
export interface Warranty {
  warranty_program_name?: string;
  warranty_program_provider_name?: string;
  term_years?: number;
  labor_rate_cents?: number;
  purchase_date?: string | null;
  effective_date?: string | null;
  expiration_date?: string | null;
}

/** One entry of `equipment.components` (V3 _equipment_component.json.jbuilder). */
export interface EquipmentComponent {
  id: number;
  name?: string;
  equipment_id?: number;
  component_type_id?: number;
  component_type_is_default?: boolean;
  archived?: boolean;
  manufacturer?: string;
  model_number?: string;
  serial_number?: string;
  warranty?: string;
  install_location?: string;
  installed_on?: string | null;
  manufactured_on?: string | null;
}

/** One entry of `equipment.service_history` (V3 _service_history.json.jbuilder). */
export interface EquipmentServiceRecord {
  id: number;
  invoice_id?: number;
  invoice_finalized_at?: string | null;
  invoice_voided?: boolean;
  invoice_line_item_id?: number;
  invoice_line_item_unit_name?: string;
  opportunity_owner_id?: number | null;
  opportunity_owner_full_name?: string | null;
  job_id?: number | null;
}

/** Domain equipment row consumed by the Equipment tab (PROF-04). */
export interface Equipment {
  id: number;
  equipment_type_name?: string;
  name?: string;
  notes?: string | null;
  archived: boolean;
  address?: Record<string, unknown> | null;
  components: EquipmentComponent[];
  warranty: Warranty | null;
  service_history: EquipmentServiceRecord[];
}

/** Snake_case mirror of one entry in the V3 equipment wire array. */
export interface EquipmentWire {
  id: number;
  equipment_type_name?: string;
  equipment_type_id?: number;
  equipment_type_is_default?: boolean;
  customer_id?: number;
  address?: Record<string, unknown> | null;
  name?: string;
  notes?: string | null;
  archived?: boolean;
  components?: EquipmentComponent[] | null;
  warranty?: Warranty | null;
  service_history?: EquipmentServiceRecord[] | null;
  links?: Record<string, string> | null;
  [extra: string]: unknown;
}

/** Map one wire row to the domain Equipment. */
function adaptOne(wire: EquipmentWire): Equipment {
  return {
    id: wire.id,
    equipment_type_name: wire.equipment_type_name,
    name: wire.name,
    notes: wire.notes ?? null,
    archived: wire.archived ?? false,
    address: wire.address ?? null,
    components: wire.components ?? [],
    warranty: wire.warranty ?? null,
    service_history: wire.service_history ?? [],
  };
}

/**
 * Pure adapter: maps the V3 equipment wire array to the domain `Equipment[]`.
 * `archived` is preserved verbatim (the Equipment tab filters "Kept" vs
 * archived client-side, RESEARCH §Equipment).
 */
export function adaptEquipment(wire: EquipmentWire[]): Equipment[] {
  return wire.map(adaptOne);
}
